import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getCommonNumberFilterParams,
  getSetDatefilterParams,
  getSetFilterParams,
} from '../../../utils/agGridFilters';
import { DateFormatter } from '../../../shared/controls/formatters';

// deterministic hash → avatar palette (same approach as the other reskinned lists)
const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

// Invoice status tone (admin-configurable strings → generic badge tones),
// mirroring the design's INV_STATUS map: draft→gray, sent→blue, paid→green,
// partial→amber, overdue→red.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('overdue') || s.includes('void') || s.includes('cancel')) return 'red';
  if (s.includes('paid') || s.includes('complet')) return 'green';
  if (s.includes('partial') || s.includes('pend') || s.includes('due')) return 'amber';
  if (s.includes('sent') || s.includes('issued')) return 'blue';
  if (s.includes('draft') || s.includes('open')) return 'gray';
  return 'gray';
};

const OwnerCell = (params) => {
  const name = params.value;
  if (!name) return null;
  return (
    <div className="gos-owner-cell">
      <span className="gos-ava sm" style={{ background: avaColor(name) }}>
        {initials(name)}
      </span>
      <span className="gos-owner-name">{name}</span>
    </div>
  );
};

const StatusCell = (params) => {
  if (!params.value) return null;
  const tone = statusTone(params.value);
  return (
    <span className={'gos-badge gos-b-' + tone}>
      <span className="gos-pip" />
      {params.value}
    </span>
  );
};

// GrowthOS re-skin: no Actions column (matches Quotes + the CRM lists' checkbox
// convention). Code is a bold link to the Invoice view page; edit/delete are
// reached from there. Cells restyled: Code link, Status badge, Owner avatar.
const useInvoiceColumnDefs = ({ gridData, owner }) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const commonNumberFilterParams = useMemo(() => getCommonNumberFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Code',
        field: 'code',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) => {
          if (!params.value) return null;
          return (
            <Link
              to={`/sales/viewInvoice?id=${params?.data?.id}`}
              state={{ ...params.data }}
              className="gos-lead-name"
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        headerName: 'Subject',
        field: 'subject',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 140,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: StatusCell,
      },
      {
        headerName: 'Invoice Date',
        field: 'invoiceDate',
        valueFormatter: DateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Grand Total',
        field: 'grandTotal',
        width: 130,
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
      },
      {
        headerName: 'Contact Name',
        field: 'contactName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Invoice Owner',
        field: 'invoiceOwner',
        minWidth: 190,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        cellRenderer: OwnerCell,
      },
    ];
  }, [commonTextFilterParams, commonNumberFilterParams, filterParams, gridData, dateFilterParams, owner]);

  return columnDefs;
};

export default useInvoiceColumnDefs;
